// ============================================================
// Live demo — public + private real-data demo account
// ============================================================
// The public "View a live demo" button AND the private demo login both
// authenticate as ONE dedicated Cognito user. Its JWT carries a FAKE
// Starlink account number (DEMO_ACCOUNT_NUMBER); the backend swaps it for
// the real "Radius Security" account, fetches LIVE telemetry, and aliases
// EVERY identifying field to an Avengers persona before the response
// leaves AWS. So the demo shows real dishes + real performance data with
// zero real PII.
//
// This account is READ-ONLY and fully anonymized server-side (no writes,
// no admin, no real names/addresses/serials).
//
// 🔐 THE PASSWORD IS NO LONGER HERE (2026-08-17). It used to ship in this
// bundle — readable by any visitor — under the "shared demo login" pattern.
// It now lives ONLY in Secrets Manager (netcom-connect/<env>/demoaccount),
// and the session is minted server-side by the demosession Lambda, which
// also holds the demo account's TOTP seed.
//
// Do NOT reintroduce a password field here. Beyond the obvious exposure, it
// cannot work: the pool is Mfa.REQUIRED and a SHARED account cannot enroll
// TOTP in a visitor's browser — the first person to scan the QR owns the
// factor and locks out everyone after them.
// See src/lib/demo-session.js and docs/MFA-CUTOVER-RUNBOOK.md § 3.

export const DEMO_ACCOUNT_NUMBER = 'DEMO-HPS-001';

// The demo persona (Harbor Point Security) is a full multi-product customer: it has
// Starlink dishes (real Radius telemetry, anonymized server-side) AND the
// Netcom Connect SIM (white-labeled KORE OmniSIM Reach multi-carrier eSIM).
// The real backend account has no Netcom Connect SIMs, so the SIM fleet is
// served client-side from src/data/mock/sims.js — see withDemoProducts() and
// sim-api.js's demo-account mock branch. Keep both products here in sync with
// the Sidebar sections that gate on session.products.
export const DEMO_PRODUCTS = ['starlink', 'sim'];

/**
 * Ensure the demo persona always advertises the full product set so the SIM
 * tab renders. The live-demo Cognito user's JWT is Starlink-only; this is a
 * UI-only override that applies ONLY to DEMO_ACCOUNT_NUMBER — real customers'
 * product scope is never touched (and the backend still enforces its own
 * assertProductScope on every real call).
 */
export function withDemoProducts(products, accountNumber) {
  // Broadened 2026-09-05: was an exact match on DEMO_ACCOUNT_NUMBER only, so
  // the ACC-DEMO-* vertical personas (Globaltrade Energy / Logistics / Retail)
  // were left product-less and lost both the Fleet and the Netcom Connect SIM
  // nav sections. Every demo persona showcases the full product set.
  const a = String(accountNumber || '').toUpperCase();
  const isDemo = a === DEMO_ACCOUNT_NUMBER || a.startsWith('ACC-DEMO-') || a.startsWith('ACC-CTO-');
  if (!isDemo) return products;
  const set = new Set([...(products || []), ...DEMO_PRODUCTS]);
  return [...set];
}

export const LIVE_DEMO = {
  // Identity only — used for the session's display email. The backend is
  // the authority on which account it actually signs in as.
  email: import.meta.env.VITE_DEMO_EMAIL || 'demo@netcomconnect.networksandcommunications.com',
};

/** True when a session/account is the anonymized live-demo account. Used to
 *  gate the live WebSocket off (it would carry no data for the fake account)
 *  and to hide write actions the backend already blocks. */
export function isDemoAccount(accountNumber) {
  return accountNumber === DEMO_ACCOUNT_NUMBER;
}
